import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { some } from 'lodash-es';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { Filter } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter.model';

export interface FilterMenuEvent {
  filter: Filter;
  isMenuOpened: boolean;
}

@Component({
  standalone: false,
  selector: 'nimbus-nimbus-filter-menu',
  templateUrl: './nimbus-filter-menu.component.html',
  styleUrls: ['./nimbus-filter-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterMenuComponent {
  @Input()
  public filterOptions: Filter[] = [];
  @Input()
  public filterOptionsSelected: Filter[] = [];
  @Input()
  public disabled: boolean = false;

  @Output()
  public filterSelected = new EventEmitter<FilterMenuEvent>();

  public isOpen: boolean = false;

  // Funnel kebab — left-edge anchored to the trigger's left edge so the panel drops directly below
  // the button and extends rightward into the toolbar. Falls back to right-edge alignment when
  // there's not enough room on the right (e.g. trigger near the viewport edge).
  public readonly cdkConnectedPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 10 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 10 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -10 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -10 },
  ];

  private isEventPropagationStopped = false;

  public readonly ICONS = ICONS;
  public readonly TranslationKeys = TranslationKeys;

  public constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public trackByFn = (_: number, item: Filter): string => item.id;

  public onTriggerClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled || !this.filterOptions?.length) {
      return;
    }
    this.isOpen = !this.isOpen;
    this.cdr.markForCheck();
  }

  public onOutsideClick(event: MouseEvent): void {
    if (this.elementRef.nativeElement.contains(event.target as Node)) {
      return;
    }
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  public onOptionSelected(filter: Filter, isMenuOpened: boolean, $event?: MouseEvent): void {
    isMenuOpened = this.isOptionSelected(filter) || isMenuOpened;
    this.isEventPropagationStopped = true;
    if (isMenuOpened) {
      $event?.stopPropagation();
      this.isEventPropagationStopped = false;
    }
    this.filterSelected.emit({ filter, isMenuOpened });
  }

  public isOptionSelected(option: Filter): boolean {
    return some(this.filterOptionsSelected, (x) => x.id === option.id);
  }

  public handleOptionBackgroundClick($event: MouseEvent, filter?: Filter): void {
    if (this.isEventPropagationStopped) {
      this.isEventPropagationStopped = false;
      return;
    }
    $event.stopPropagation();
  }
}
